import React, { useState, useEffect } from 'react';
import './dashboardSummary.css';
import { useNavigate } from 'react-router-dom';
import { Box, Typography} from '@mui/material';
import { styled } from '@mui/system';

// ── Module-level cache ────────────────────────────────────────────────────
// Survives component unmount/remount (e.g. switching menus and coming back
// to Dashboard) since it lives outside the component, in the JS module scope.
// It's cleared only on a full page reload. This lets the dashboard render
// instantly with last-known data instead of showing the spinner every time.
let dashboardCache = null;
const DASHBOARD_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const getCached = (key, fallback) => {
  const isFresh = dashboardCache && Date.now() - dashboardCache.timestamp < DASHBOARD_CACHE_TTL;
  const value = isFresh ? dashboardCache[key] : undefined;
  return value !== undefined ? value : fallback;
};

/* ── Count-up hook ── */
function useCountUp(target, duration = 1000) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target && target !== 0) { setCount(target); return; }
    const num = parseFloat(String(target).replace(/,/g, ''));
    if (isNaN(num) || num === 0) { setCount(0); return; }
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(num);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return count;
}

function CountUp({ value, style, className, localize = true }) {
  const num = useCountUp(value);
  const display = localize ? Number(num).toLocaleString() : num;
  return <span style={style} className={className}>{display}</span>;
}

const DashboardSummary = ({ setActiveMenu, setActiveSubMenu }) => {
  const navigate = useNavigate();
    const SpinningLogo = styled("img")(({ theme }) => ({
  width: "150px",
  height: "150px",
}));

  const handleCardClick = (item) => {
    // Set active menu states
    switch (item.title) {
      case 'Orders':
        setActiveMenu('orders');
        setActiveSubMenu('allOrders');
        navigate('/adminDashBoard/orders/allOrders');
        break;
      case 'Car Inventory':
        setActiveMenu('cars');
        setActiveSubMenu(null);
        navigate('/adminDashBoard/cars/allCarListed');
        break;
      case 'Shipping Inquiries':
        setActiveMenu('inquiries');
        setActiveSubMenu('allInquiries');
        navigate('/adminDashBoard/inquiries/AllInquiries');
        break;
      case 'Manage Popular Cars':
        setActiveMenu('inquiries');
        setActiveSubMenu('allInquiries');
        navigate('/adminDashBoard/popularCars ');
        break;
      case 'Manage Affordable Cars':
        setActiveMenu('inquiries');
        setActiveSubMenu('allInquiries');
        navigate('/adminDashBoard/affordableCars');
        break;
      case 'Total Users':
        setActiveMenu('users');
        setActiveSubMenu('allUsers');
        navigate('/adminDashBoard/users/allUsers');
        break;
      case 'Inquiry Tickets':
        setActiveMenu('inquiries');
        setActiveSubMenu('inquiryTickets');
        navigate('/adminDashBoard/inquiries/inquiryTickets');
        break;
      case 'Order Tickets':
        setActiveMenu('orders');
        setActiveSubMenu('orderTickets');
        navigate('/adminDashBoard/orders/orderTickets');
        break;
      case 'Total Reviews':
        setActiveMenu('reviews');
        setActiveSubMenu(null);
        navigate('/adminDashBoard/reviews');
        break;
      case 'Geographical Setup':
        setActiveMenu('geography');
        setActiveSubMenu(null);
        navigate('/adminDashBoard/geography');
        break;
      case 'Shipment Fees':
        setActiveMenu('shipment');
        setActiveSubMenu('addFee');
        navigate('/adminDashBoard/shipment/addFee');
        break;
      case 'Set Price Margin':
        setActiveMenu('priceMargin');
        setActiveSubMenu(null);
        navigate('/adminDashBoard/priceMargin');
        break;
      default:
        setActiveMenu('dashboard');
        setActiveSubMenu(null);
    }
  };

  const defaultSummaryData = [
    {
      title: 'Orders',
      count: 0,
      icon: '📦',
      link: '/adminDashBoard/orders/allOrders',
      cardBg: '#475569',
      showCount: true
    },
    {
      title: 'Car Inventory',
      count: 0,
      icon: '🚗',
      link: '/addNewCar',
      cardBg: '#475569',
      showCount: false,
      showCarInventory: true
    },
    {
      title: 'Shipping Inquiries',
      count: 0,
      icon: '📬',
      link: '/adminDashBoard/inquiries/AllInquiries',
      cardBg: '#475569',
      showCount: false,
      showInquiryCounts: true
    },
    {
      title: 'Manage Popular Cars',
      icon: '⭐',
      link: '/adminDashBoard/popularCars',
      cardBg: '#475569',
      showCount: false
    },
    {
      title: 'Manage Affordable Cars',
      icon: '💰',
      link: '/adminDashBoard/affordableCars',
      cardBg: '#475569',
      showCount: false
    },
    {
      title: 'Total Users',
      count: 0,
      icon: '👥',
      link: '/adminDashBoard/allUsers',
      cardBg: '#475569',
      showCount: true,
      showUserRoles: true
    },
    {
      title: 'Inquiry Tickets',
      count: 0,
      icon: '🎫',
      link: '/adminDashBoard/inquiries/inquiryTickets',
      cardBg: '#475569',
      showCount: true,
      statusCounts: { open: 0, in_progress: 0, re_open: 0, closed: 0 }
    },
    {
      title: 'Order Tickets',
      count: 0,
      icon: '🗂️',
      link: '/adminDashBoard/orders/orderTickets',
      cardBg: '#475569',
      showCount: true,
      statusCounts: { open: 0, in_progress: 0, re_open: 0, closed: 0 }
    },
    {
      title: 'Total Reviews',
      count: 0,
      icon: '⭐',
      link: '/adminDashBoard/reviews',
      cardBg: '#475569',
      showCount: true,
      reviewStatusCounts: { approved: 0, pending: 0, rejected: 0, listed: 0 }
    },
    {
      title: 'Geographical Setup',
      icon: '🌍',
      link: '/adminDashBoard/geographicalSetup',
      cardBg: '#475569',
      showCount: false
    },
    {
      title: 'Shipment Fees',
      icon: '🚢',
      link: '/adminDashBoard/shipmentFees',
      cardBg: '#475569',
      showCount: false
    },
    {
      title: 'Set Price Margin',
      icon: '💹',
      link: '/adminDashBoard/setPriceMargin',
      cardBg: '#475569',
      showCount: false
    },
  ];
  const [summaryData, setSummaryData] = useState(() => getCached('summaryData', defaultSummaryData));
  const [loading, setLoading] = useState(() => !(dashboardCache && Date.now() - dashboardCache.timestamp < DASHBOARD_CACHE_TTL));
  const [secondaryLoading, setSecondaryLoading] = useState(() => !(dashboardCache && Date.now() - dashboardCache.timestamp < DASHBOARD_CACHE_TTL));
  const [error, setError] = useState(null);

  // ── Popular & Affordable Cars mini-card data ──────────────────────────────
  const [popularCarsData, setPopularCarsData] = useState(() => getCached('popularCarsData', {
    totalAvailable: 0,
    popularCount: 0,
  }));
  const [affordableCarsData, setAffordableCarsData] = useState(() => getCached('affordableCarsData', {
    totalAvailable: 0,
    affordableCount: 0
  }));

  // ── Users role breakdown counts ──────────────────────────────────────────
  const [userRoleCounts, setUserRoleCounts] = useState(() => getCached('userRoleCounts', { users: 0, admins: 0, superAdmins: 0, newUsers: 0 }));

  // ── Unread notification counts for ticket cards ───────────────────────────
  const [inquiryUnreadTotal, setInquiryUnreadTotal] = useState(() => getCached('inquiryUnreadTotal', 0));
  const [orderUnreadTotal, setOrderUnreadTotal] = useState(() => getCached('orderUnreadTotal', 0));

  // ── Car Inventory counts (total / available / sold) ──────────────────────
  const [carInventoryCounts, setCarInventoryCounts] = useState(() => getCached('carInventoryCounts', { total: 0, available: 0, sold: 0 }));

  // ── Inquiry counts (shipping / general / total) ───────────────────────────
  const [inquiryCounts, setInquiryCounts] = useState(() => getCached('inquiryCounts', { shipping: 0, general: 0, total: 0 }));

  // ── "New" counts ────────────────────────────────────────────────────────
  const [newOrdersCount, setNewOrdersCount] = useState(() => getCached('newOrdersCount', 0)); // Real unread count from isOrderRead field
  const [newInquiriesCount, setNewInquiriesCount] = useState(() => getCached('newInquiriesCount', 0)); // Real unread count from isInquiryRead field

  // ── Geographical Setup counts ─────────────────────────────────────────────
  const [geoCounts, setGeoCounts] = useState(() => getCached('geoCounts', { active: 0, inactive: 0, total: 0 }));
  const [portCounts, setPortCounts] = useState(() => getCached('portCounts', { active: 0, inactive: 0, total: 0 }));

  // ── Shipment Fees counts ──────────────────────────────────────────────────
  const [shipmentCounts, setShipmentCounts] = useState(() => getCached('shipmentCounts', { configured: 0, pending: 0, total: 0 }));

  // ── Set Price Margin ──────────────────────────────────────────────────────
  const [currentMargin, setCurrentMargin] = useState(() => getCached('currentMargin', null));

  const getToken = () => {
    const knownKeys = ["adminAuthToken", "authToken", "auth_token", "token", "accessToken", "jwt", "jwtToken", "adminToken", "userToken", "access_token"];
    for (const key of knownKeys) {
      const val = localStorage.getItem(key);
      if (val && val.startsWith("ey")) return val;
    }
    for (let i = 0; i < localStorage.length; i++) {
      const val = localStorage.getItem(localStorage.key(i));
      if (val && val.startsWith("ey") && val.includes(".")) return val;
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const adminToken = localStorage.getItem('adminAuthToken') || token;

        // ═══════════════════════════════════════════════════════════════════
        // WAVE 1 — CRITICAL: only what the top two rows of cards need.
        // Fired first, in parallel, and the page unblocks (loading = false)
        // as soon as this resolves — secondary cards fill in afterward.
        // ═══════════════════════════════════════════════════════════════════
        const [
          ordersRes, carsRes, inquiriesRes, usersRes,
          allInquiriesRes, allOrdersRes,
          usersAllRes,
          inquiryTicketsRes, orderTicketsRes,
          popularAvailableRes, popularListRes, affordableAvailableRes, affordableListRes,
          generalInquiryCountRes,
        ] = await Promise.allSettled([
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-total-order', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/available-car-count', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-count-of-shipment-inquiry', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-count-of-all-users', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-all-inquiry?page=0&size=1000', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${adminToken}` }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-all-orders/placed-orders?page=0&size=1000', {
            headers: { 'accept': 'application/json' }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-user/get-all-user-and-admins', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${adminToken}` }
          }),
          fetch('https://car.amar.co.jp/api/dashboard/raise-ticket/get-all-tickets', {
            headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` }
          }),
          fetch('https://car.amar.co.jp/api/dashboard/order-ticket/get-all-ticket', {
            headers: { 'accept': 'application/json' }
          }),
          fetch('https://car.amar.co.jp/api/master/popular-car/get-available-models?page=0&size=100', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/master/popular-car/get-all_popular_car?page=0&size=500', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/master/affordable-car/get-available-models?page=0&size=100', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/master/affordable-car/get-all-affordable-car?page=0&size=500', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
          fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-count-of-general-inquiry', {
            headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
          }),
        ]);

        // Helper: safely pull JSON out of a Promise.allSettled result
        const okJson = async (settled, fallback = null) => {
          if (settled.status !== 'fulfilled' || !settled.value || !settled.value.ok) return fallback;
          try { return await settled.value.json(); } catch { return fallback; }
        };

        const [
          ordersData, carsData, inquiriesData, usersData,
          allInquiryData, allOrdersData,
          usersAllData,
          inquiryTicketsData, orderTicketsData,
          popularAvailableData, popularListData, affordableAvailableData, affordableListData,
          generalInquiryCountData,
        ] = await Promise.all([
          okJson(ordersRes), okJson(carsRes), okJson(inquiriesRes), okJson(usersRes),
          okJson(allInquiriesRes), okJson(allOrdersRes),
          okJson(usersAllRes),
          okJson(inquiryTicketsRes), okJson(orderTicketsRes),
          okJson(popularAvailableRes), okJson(popularListRes), okJson(affordableAvailableRes), okJson(affordableListRes),
          okJson(generalInquiryCountRes),
        ]);

        if (!ordersData || !carsData || !inquiriesData || !usersData) {
          throw new Error('One or more core API requests failed');
        }

        // ── Shipping Inquiries counts ─────────────────────────────────────────
        const shippingCount = inquiriesData.totalShipmentInquiry || 0;
        const totalInquiryCount = allInquiryData?.totalInquiry     || 0;
        // General count from its own API; fall back to (total - shipping) if that call fails
        const generalCount  = typeof generalInquiryCountData?.totalGeneralInquiry === 'number'
          ? generalInquiryCountData.totalGeneralInquiry
          : Math.max(0, totalInquiryCount - shippingCount);
        const unreadInquiryCount = (allInquiryData?.data || []).filter(
          inq => inq.isInquiryRead === false
        ).length;

        const newInquiryCounts = { shipping: shippingCount, general: generalCount, total: totalInquiryCount };
        setInquiryCounts(newInquiryCounts);
        setNewInquiriesCount(unreadInquiryCount);

        // ── Unread orders (isOrderRead field) ─────────────────────────────────
        const unreadOrdersCount = (allOrdersData?.data || []).filter(
          o => o.isOrderRead === false
        ).length;
        setNewOrdersCount(unreadOrdersCount);

        // ── User role breakdown ───────────────────────────────────────────────
        const userList = usersAllData?.userList || [];
        const newUserRoleCounts = {
          users:       userList.filter(u => u.roleName && !u.roleName.toLowerCase().includes('admin') && !u.roleName.toLowerCase().includes('super')).length,
          admins:      userList.filter(u => u.roleName && u.roleName.toLowerCase().includes('admin') && !u.roleName.toLowerCase().includes('super')).length,
          superAdmins: userList.filter(u => u.roleName && u.roleName.toLowerCase().includes('super')).length,
          newUsers:    userList.filter(u => u.isNewUser === true).length,
        };
        setUserRoleCounts(newUserRoleCounts);

        // ── Popular & Affordable Cars data ───────────────────────────────────
        const popularAllCars = popularAvailableData?.data?.carModels || [];
        const popularCarsList = popularListData?.data || [];
        const affordableAllCars = affordableAvailableData?.data?.carModels || [];
        const affordableCarsList = affordableListData?.data || [];

        const newPopularCarsData = {
          totalAvailable: popularAvailableData?.data?.totalRecords || popularAllCars.length,
          popularCount: popularCarsList.length
        };
        const newAffordableCarsData = {
          totalAvailable: affordableAvailableData?.data?.totalRecords || affordableAllCars.length,
          affordableCount: affordableCarsList.length
        };
        setPopularCarsData(newPopularCarsData);
        setAffordableCarsData(newAffordableCarsData);

        // ── Car Inventory: available count is ready now; total/sold arrive in
        //    the secondary wave below (keep whatever was there before, e.g. cache) ──
        setCarInventoryCounts(prev => ({ ...prev, available: carsData.totalAvailableCars || 0 }));

        // Compute updated summary data for the critical cards, synchronously
        // (not inside a setState updater — see earlier bug note) so it's ready
        // for the cache write. Reviews/tickets status counts for cases 6-8 are
        // filled in during the secondary wave since Tickets/Reviews cards sit
        // lower on the page; cases 6-7 (tickets) ARE critical though, so we
        // compute them here using the ticket lists already fetched above.
        const computedSummaryData = summaryData.map((item, index) => {
          switch (index) {
            case 0: // Total Orders
              return { ...item, count: ordersData.totalOrdersPlaced || 0 };
            case 1: // Total Cars Listed
              return { ...item, count: carsData.totalAvailableCars || 0 };
            case 2: // Total Inquiries
              return { ...item, count: inquiriesData.totalShipmentInquiry || 0 };
            case 3: // Manage Popular Cars - no count update
              return item;
            case 4: // Manage Affordable Cars - no count update
              return item;
            case 5: // Total Users
              return { ...item, count: usersData.totalUserCount || 0 };
            case 6: { // Inquiry Tickets
              const inquiryList = inquiryTicketsData?.data || [];
              const getInquiryStatus = (t) => {
                const s = (t.ticketStatus || '').toUpperCase();
                if (s === 'REOPEN') return 're-open';
                if (s === 'CLOSED') return 'closed';
                if (s === 'IN_PROGRESS') return 'in-progress';
                if (s === 'OPEN' && (t.ticketReopenBy || t.reopenCount > 0)) return 're-open';
                if (s === 'OPEN') return 'open';
                return 'open';
              };
              const inquiryStatusCounts = {
                open:        inquiryList.filter(t => getInquiryStatus(t) === 'open').length,
                in_progress: inquiryList.filter(t => getInquiryStatus(t) === 'in-progress').length,
                re_open:     inquiryList.filter(t => getInquiryStatus(t) === 're-open').length,
                closed:      inquiryList.filter(t => getInquiryStatus(t) === 'closed').length,
              };
              return { ...item, count: inquiryTicketsData?.totalTickets ?? inquiryList.length ?? 0, statusCounts: inquiryStatusCounts };
            }
            case 7: { // Order Tickets
              const orderList = orderTicketsData?.data || [];
              const getOrderStatus = (t) => {
                const s = (t.ticketStatus || '').toUpperCase();
                if (s === 'REOPEN') return 're-open';
                if (s === 'CLOSED') return 'closed';
                if (s === 'IN_PROGRESS') return 'in-progress';
                if (s === 'OPEN' && (t.ticketReopenBy || t.reopenCount > 0)) return 're-open';
                if (s === 'OPEN') return 'open';
                return 'open';
              };
              const orderStatusCounts = {
                open:        orderList.filter(t => getOrderStatus(t) === 'open').length,
                in_progress: orderList.filter(t => getOrderStatus(t) === 'in-progress').length,
                re_open:     orderList.filter(t => getOrderStatus(t) === 're-open').length,
                closed:      orderList.filter(t => getOrderStatus(t) === 'closed').length,
              };
              return { ...item, count: orderList.length || 0, statusCounts: orderStatusCounts };
            }
            default:
              return item; // case 8 (Total Reviews) filled in by the secondary wave
          }
        });
        setSummaryData(computedSummaryData);

        // Critical wave done — unblock the page. Secondary cards below the
        // fold (Reviews, Geographical Setup, Shipment Fees, Set Price Margin,
        // Car Inventory total/sold) still show their previous/placeholder
        // values and fill in a moment later without blocking this render.
        setLoading(false);
        setSecondaryLoading(true);

        // ═══════════════════════════════════════════════════════════════════
        // WAVE 2 — SECONDARY: everything else, fetched after first paint.
        // ═══════════════════════════════════════════════════════════════════
        let newCurrentMargin = null;
        try {
          const [
            carTotalRes, carSoldRes,
            geoRes, portRes,
            shipConfiguredRes, shipPendingRes,
            reviewsRes, listedReviewsRes,
            marginRes,
          ] = await Promise.allSettled([
            fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-list-of-car?page=0&size=1', {
              headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
            }),
            fetch('https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-list-of-sold-car?page=0&size=1', {
              headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
            }),
            fetch('https://car.amar.co.jp/api/authorization/shipping/all-countries', {
              headers: { 'accept': 'application/json' }
            }),
            fetch('https://car.amar.co.jp/api/authorization/shipping/ports', {
              headers: { 'accept': 'application/json' }
            }),
            fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/ports/configured', {
              headers: { 'accept': 'application/json' }
            }),
            fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/ports/not-configured', {
              headers: { 'accept': 'application/json' }
            }),
            fetch('https://car.amar.co.jp/api/review-rating/review-rating/admin/all-reviews?page=0&size=9999', {
              headers: { 'accept': 'application/json' }
            }),
            fetch('https://car.amar.co.jp/api/review-rating/approved-review/get-list?page=0&size=9999', {
              headers: { 'accept': 'application/json' }
            }),
            fetch('https://car.amar.co.jp/api/dashboard/car-price/get/latest/margin', {
              headers: { 'accept': 'application/json' }
            }),
          ]);

          const [
            carTotalData, carSoldData,
            geoDataRaw, portDataRaw,
            shipConfiguredData, shipPendingData,
            reviewsData, listedReviewsData,
            marginData,
          ] = await Promise.all([
            okJson(carTotalRes), okJson(carSoldRes),
            okJson(geoRes, []), okJson(portRes, []),
            okJson(shipConfiguredRes, []), okJson(shipPendingRes, []),
            okJson(reviewsRes), okJson(listedReviewsRes),
            okJson(marginRes),
          ]);

          // ── Car Inventory (fill in total + sold now) ───────────────────────
          const newCarInventoryCounts = {
            available: carsData.totalAvailableCars || 0,
            total:     carTotalData?.pagination?.totalElements ?? 0,
            sold:      carSoldData?.pagination?.totalElements  ?? 0,
          };
          setCarInventoryCounts(newCarInventoryCounts);

          // ── Geographical Setup counts ──────────────────────────────────────
          const countryList = Array.isArray(geoDataRaw) ? geoDataRaw : (geoDataRaw?.data && Array.isArray(geoDataRaw.data) ? geoDataRaw.data : []);
          const portList    = Array.isArray(portDataRaw) ? portDataRaw : (portDataRaw?.data && Array.isArray(portDataRaw.data) ? portDataRaw.data : []);
          const isCountryActive = (c) => c.isActive === true || c.isActive === 1 || c.status === 'Active' || c.status === 'active';
          const newGeoCounts = {
            active:   countryList.filter(c => isCountryActive(c)).length,
            inactive: countryList.filter(c => !isCountryActive(c)).length,
            total:    countryList.length
          };
          const newPortCounts = { active: portList.length, inactive: 0, total: portList.length };
          setGeoCounts(newGeoCounts);
          setPortCounts(newPortCounts);

          // ── Shipment Fees counts ────────────────────────────────────────────
          const groupedConfigured = new Set(
            (shipConfiguredData || []).map(item => `${item.countryName}__${item.portName}`)
          ).size;
          const pendingCount = (shipPendingData || []).length;
          const newShipmentCounts = { configured: groupedConfigured, pending: pendingCount, total: groupedConfigured + pendingCount };
          setShipmentCounts(newShipmentCounts);

          // ── Set Price Margin ────────────────────────────────────────────────
          newCurrentMargin = marginData?.totalMargin ?? null;
          setCurrentMargin(newCurrentMargin);

          // ── Total Reviews card (case 8) ─────────────────────────────────────
          setSummaryData(prev => prev.map((item, index) => {
            if (index !== 8) return item;
            const reviewList = reviewsData?.data?.reviews || [];
            const listedCount = listedReviewsData?.data?.totalRecords || 0;
            const reviewStatusCounts = {
              approved: reviewList.filter(r => (r.reviewStatus || '').toUpperCase() === 'APPROVED').length,
              pending:  reviewList.filter(r => (r.reviewStatus || '').toUpperCase() === 'PENDING').length,
              rejected: reviewList.filter(r => (r.reviewStatus || '').toUpperCase() === 'REJECTED').length,
              listed:   listedCount,
            };
            return { ...item, count: reviewList.length || 0, reviewStatusCounts };
          }));

          // Update the cache's summaryData snapshot to include the reviews card too
          dashboardCache = dashboardCache ? {
            ...dashboardCache,
            carInventoryCounts: newCarInventoryCounts,
            geoCounts: newGeoCounts,
            portCounts: newPortCounts,
            shipmentCounts: newShipmentCounts,
            currentMargin: newCurrentMargin,
          } : dashboardCache;
        } catch (secErr) {
          console.error('Error fetching secondary dashboard data:', secErr);
        }
        setSecondaryLoading(false);

        // ── Unread chat-message counts for ticket cards ───────────────────────
        // (needs the ticket lists fetched in the critical wave above)
        let newInquiryUnreadTotal = 0;
        let newOrderUnreadTotal = 0;
        if (token) {
          const inquiryTicketList = inquiryTicketsData?.data || [];
          const orderTicketList   = orderTicketsData?.data || [];

          const unreadResults = await Promise.allSettled([
            ...inquiryTicketList.map(ticket =>
              fetch(`https://car.amar.co.jp/api/dashboard/inquiry-chat/unread-count/${ticket.ticketNumber}`, {
                headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` }
              }).then(r => r.ok ? r.json() : { unreadCount: 0 }).catch(() => ({ unreadCount: 0 }))
            ),
            ...orderTicketList.map(ticket =>
              fetch(`https://car.amar.co.jp/api/dashboard/order-ticket-chat/unread-count/${ticket.orderTicketNumber}`, {
                headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` }
              }).then(r => r.ok ? r.json() : { unreadCount: 0 }).catch(() => ({ unreadCount: 0 }))
            ),
          ]);

          const inquiryResults = unreadResults.slice(0, inquiryTicketList.length);
          const orderResults   = unreadResults.slice(inquiryTicketList.length);

          newInquiryUnreadTotal = inquiryResults.reduce((sum, r) => sum + (r.status === 'fulfilled' ? (r.value?.unreadCount || 0) : 0), 0);
          newOrderUnreadTotal   = orderResults.reduce((sum, r) => sum + (r.status === 'fulfilled' ? (r.value?.unreadCount || 0) : 0), 0);

          setInquiryUnreadTotal(newInquiryUnreadTotal);
          setOrderUnreadTotal(newOrderUnreadTotal);
        }

        // ── Save everything to the module-level cache for instant re-renders
        //    next time this component mounts (e.g. switching menus and back) ──
        dashboardCache = {
          ...dashboardCache,
          timestamp: Date.now(),
          summaryData: computedSummaryData,
          popularCarsData: newPopularCarsData,
          affordableCarsData: newAffordableCarsData,
          userRoleCounts: newUserRoleCounts,
          inquiryUnreadTotal: newInquiryUnreadTotal,
          orderUnreadTotal: newOrderUnreadTotal,
          inquiryCounts: newInquiryCounts,
          newOrdersCount: unreadOrdersCount,
          newInquiriesCount: unreadInquiryCount,
        };
      } catch (err) {
        setError(err.message);
        setLoading(false);
        setSecondaryLoading(false);
        console.error('Error fetching dashboard data:', err);
      }
    };

    let isMounted = true;
    let timeoutId = null;

    const runAndReschedule = async () => {
      await fetchData();
      // Only schedule the next cycle AFTER this one fully finishes — prevents
      // overlapping cycles from piling up requests if a fetch ever runs long.
      if (isMounted) {
        timeoutId = setTimeout(runAndReschedule, 30000);
      }
    };

    runAndReschedule();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    window.location.reload();
  };

  if (loading) {
    return (
         <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2
        }}
      >
        <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        <Typography variant="body1">Loading car data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <div className="ds-container ds-error">
        <div className="ds-error-icon">⚠️</div>
        <p>Error: {error}</p>
        <button onClick={handleRefresh} className="ds-retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="ds-container">
      <div className="ds-header">
        <h2 className="ds-title">Dashboard Overview</h2>
        <div className="ds-actions">
          {/* <button className="ds-refresh-btn" onClick={handleRefresh}>
            Refresh Data
          </button> */}
          <div className="ds-time-indicator">
            {secondaryLoading ? 'Loading remaining widgets…' : 'Last updated: Just now'}
          </div>
        </div>
      </div>

      <div className="ds-cards-grid">
        {summaryData.map((item, index) => (
          <div
            key={index}
            className={`ds-summary-card`}
            style={{ background: '#475569' }}
            onClick={() => handleCardClick(item)}
          >

            {/* Top: icon + title (+ big count, + message icon for ticket cards) */}
            <div className="ds-card-top">
              <div className="ds-card-icon-wrap">
                <span className="ds-card-icon">{item.icon}</span>
              </div>
              <span className="ds-card-label">{item.title}</span>
              {(item.statusCounts || item.reviewStatusCounts || item.showUserRoles) && (
                <span className="ds-card-header-count">
                  <CountUp value={item.count} localize={item.showUserRoles ? true : false} />
                </span>
              )}
              {item.title === 'Inquiry Tickets' && (
                <div className="ds-notif-icon ds-notif-icon-inline">
                  💬
                  {inquiryUnreadTotal > 0 && (
                    <div className="ds-notif-badge">
                      {inquiryUnreadTotal > 99 ? '99+' : inquiryUnreadTotal}
                    </div>
                  )}
                </div>
              )}
              {item.title === 'Order Tickets' && (
                <div className="ds-notif-icon ds-notif-icon-inline">
                  💬
                  {orderUnreadTotal > 0 && (
                    <div className="ds-notif-badge">
                      {orderUnreadTotal > 99 ? '99+' : orderUnreadTotal}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Middle */}
            {item.statusCounts ? (
              <div className="ds-ticket-mid">
                <div className="ds-ticket-status-grid">
                  <div className="ds-ticket-status-item ds-status-open">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">New</span>
                    {item.statusCounts.open > 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '22px', height: '20px', padding: '0 6px', borderRadius: '999px', background: '#dc2626', color: '#fff', fontSize: '13px', fontWeight: 700 }}><CountUp value={item.statusCounts.open} localize={false} /></span>
                    ) : (
                      <span className="ds-status-num"><CountUp value={item.statusCounts.open} localize={false} /></span>
                    )}
                  </div>
                  <div className="ds-ticket-status-item ds-status-inprogress">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">In-Progress</span>
                    <span className="ds-status-num"><CountUp value={item.statusCounts.in_progress} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-reopen">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Re-Open</span>
                    <span className="ds-status-num"><CountUp value={item.statusCounts.re_open} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-closed">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Closed</span>
                    <span className="ds-status-num"><CountUp value={item.statusCounts.closed} localize={false} /></span>
                  </div>
                </div>
              </div>
            ) : item.reviewStatusCounts ? (
              <div className="ds-ticket-mid">
                <div className="ds-ticket-status-grid">
                  <div className="ds-ticket-status-item ds-status-inprogress">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Approved</span>
                    <span className="ds-status-num"><CountUp value={item.reviewStatusCounts.approved} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-open">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Pending</span>
                    {item.reviewStatusCounts.pending > 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '22px', height: '20px', padding: '0 6px', borderRadius: '999px', background: '#dc2626', color: '#fff', fontSize: '13px', fontWeight: 700 }}><CountUp value={item.reviewStatusCounts.pending} localize={false} /></span>
                    ) : (
                      <span className="ds-status-num"><CountUp value={item.reviewStatusCounts.pending} localize={false} /></span>
                    )}
                  </div>
                  <div className="ds-ticket-status-item ds-status-closed">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Rejected</span>
                    <span className="ds-status-num"><CountUp value={item.reviewStatusCounts.rejected} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-reopen">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Listed</span>
                    <span className="ds-status-num"><CountUp value={item.reviewStatusCounts.listed} localize={false} /></span>
                  </div>
                </div>
              </div>
            ) : item.showUserRoles ? (
              <div className="ds-ticket-mid">
                <div className="ds-ticket-status-grid">
                  <div className="ds-ticket-status-item ds-status-inprogress">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Users</span>
                    <span className="ds-status-num"><CountUp value={userRoleCounts.users} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-open">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Admins</span>
                    <span className="ds-status-num"><CountUp value={userRoleCounts.admins} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-reopen">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">Super Admin</span>
                    <span className="ds-status-num"><CountUp value={userRoleCounts.superAdmins} localize={false} /></span>
                  </div>
                  <div className="ds-ticket-status-item ds-status-closed">
                    <span className="ds-status-dot"></span>
                    <span className="ds-status-label">New Users</span>
                    {userRoleCounts.newUsers > 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '22px', height: '20px', padding: '0 6px', borderRadius: '999px', background: '#dc2626', color: '#fff', fontSize: '13px', fontWeight: 700 }}><CountUp value={userRoleCounts.newUsers} localize={false} /></span>
                    ) : (
                      <span className="ds-status-num"><CountUp value={userRoleCounts.newUsers} localize={false} /></span>
                    )}
                  </div>
                </div>
              </div>
            ) : item.showCarInventory ? (
              <div style={{ display: 'flex', width: '100%', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                    {secondaryLoading ? '—' : <CountUp value={carInventoryCounts.total} />}
                  </span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Total Cars</span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={carInventoryCounts.available} /></span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Available</span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                    {secondaryLoading ? '—' : <CountUp value={carInventoryCounts.sold} />}
                  </span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Sold</span>
                </div>
              </div>
            ) : item.showInquiryCounts ? (
              <div style={{ display: 'flex', width: '100%', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={inquiryCounts.total} /></span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Total</span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={inquiryCounts.shipping} /></span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Shipping</span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={inquiryCounts.general} /></span>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>General</span>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 6px', gap: '4px' }}>
                  {newInquiriesCount > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '26px', height: '22px', padding: '0 7px', borderRadius: '999px', background: '#dc2626', color: '#fff', fontSize: '14px', fontWeight: 700 }}><CountUp value={newInquiriesCount} localize={false} /></span>
                  ) : (
                    <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={newInquiriesCount} localize={false} /></span>
                  )}
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>New</span>
                </div>
              </div>
            ) : item.title === 'Orders' ? (
              <div className="ds-manage-stats">
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={item.count} /></span>
                  <span className="ds-manage-stat-label">Total Orders</span>
                </div>
                <div className="ds-manage-stat-divider"></div>
                <div className="ds-manage-stat-item">
                  {newOrdersCount > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '26px', height: '22px', padding: '0 7px', borderRadius: '999px', background: '#dc2626', color: '#fff', fontSize: '14px', fontWeight: 700 }}><CountUp value={newOrdersCount} localize={false} /></span>
                  ) : (
                    <span className="ds-manage-stat-num"><CountUp value={newOrdersCount} localize={false} /></span>
                  )}
                  <span className="ds-manage-stat-label">New Order</span>
                </div>
              </div>
            ) : item.showCount ? (
              <div className="ds-card-count"><CountUp value={item.count} /></div>
            ) : item.title === 'Manage Popular Cars' ? (
              <div className="ds-manage-stats">
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={popularCarsData.totalAvailable} /></span>
                  <span className="ds-manage-stat-label">Total Available</span>
                </div>
                <div className="ds-manage-stat-divider"></div>
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={popularCarsData.popularCount} localize={false} /></span>
                  <span className="ds-manage-stat-label">Popular Cars</span>
                </div>
              </div>
            ) : item.title === 'Manage Affordable Cars' ? (
              <div className="ds-manage-stats">
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={affordableCarsData.totalAvailable} /></span>
                  <span className="ds-manage-stat-label">Total Available</span>
                </div>
                <div className="ds-manage-stat-divider"></div>
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={affordableCarsData.affordableCount} localize={false} /></span>
                  <span className="ds-manage-stat-label">Affordable Cars</span>
                </div>
              </div>
            ) : item.title === 'Geographical Setup' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(0,0,0,0.22)', borderRadius: '9px', padding: '7px 10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '2px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={geoCounts.total} localize={false} /></span>
                    <span style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Total Countries</span>
                  </div>
                  <div style={{ width: '1px', height: '26px', background: 'rgba(255,255,255,0.28)', margin: '0 4px' }}></div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '2px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={geoCounts.active} localize={false} /></span>
                    <span style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Active</span>
                  </div>
                  <div style={{ width: '1px', height: '26px', background: 'rgba(255,255,255,0.28)', margin: '0 4px' }}></div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '2px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={geoCounts.inactive} localize={false} /></span>
                    <span style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Inactive</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(0,0,0,0.22)', borderRadius: '9px', padding: '7px 10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '2px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={portCounts.total} localize={false} /></span>
                    <span style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Total Ports</span>
                  </div>
                  <div style={{ width: '1px', height: '26px', background: 'rgba(255,255,255,0.28)', margin: '0 4px' }}></div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '2px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={portCounts.active} localize={false} /></span>
                    <span style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Active</span>
                  </div>
                  <div style={{ width: '1px', height: '26px', background: 'rgba(255,255,255,0.28)', margin: '0 4px' }}></div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '2px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={portCounts.inactive} localize={false} /></span>
                    <span style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Inactive</span>
                  </div>
                </div>
              </div>
            ) : item.title === 'Shipment Fees' ? (
              <div className="ds-manage-stats">
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={shipmentCounts.configured} localize={false} /></span>
                  <span className="ds-manage-stat-label">Configured</span>
                </div>
                <div className="ds-manage-stat-divider"></div>
                <div className="ds-manage-stat-item">
                  <span className="ds-manage-stat-num"><CountUp value={shipmentCounts.pending} localize={false} /></span>
                  <span className="ds-manage-stat-label">Pending</span>
                </div>
              </div>
            ) : item.title === 'Set Price Margin' ? (
              currentMargin !== null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Circular gauge */}
                  <div style={{ position: 'relative', width: '68px', height: '68px', flexShrink: 0 }}>
                    <svg viewBox="0 0 72 72" style={{ transform: 'rotate(-90deg)', width: '68px', height: '68px' }}>
                      <circle cx="36" cy="36" r="28" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="7" />
                      <circle cx="36" cy="36" r="28" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="7"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - Math.min(currentMargin, 100) / 100)}`}
                      />
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '17px', fontWeight: 800, color: '#fff', lineHeight: 1 }}><CountUp value={currentMargin} localize={false} /></span>
                      <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>%</span>
                    </div>
                  </div>
                  {/* Labels */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>Current Margin</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>Applied to all<br />inventory vehicles</div>
                  </div>
                </div>
              ) : (
                <div className="ds-card-tagline">Manage your listings</div>
              )
            ) : (
              <div className="ds-card-tagline">Manage your listings</div>
            )}

            {/* Footer */}
            {/* <div className="ds-card-footer-wrap">
              <div className="ds-fdiv"></div>
              <div className="ds-card-footer">
                View details <span className="ds-arrow">→</span>
              </div>
            </div> */}

          </div>
        ))}
      </div>

    </div>
  );
};

export default DashboardSummary;